import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Input,
  PLATFORM_ID,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { DeviceService } from "@core/services/device.service";
import { SwipeRefreshManagerService } from "@core/services/swipe-refresh-manager.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { Subscription } from "rxjs";

@Component({
  selector: "astrobin-swipe-to-refresh",
  templateUrl: "./swipe-to-refresh.component.html",
  styleUrls: ["./swipe-to-refresh.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwipeToRefreshComponent implements OnInit, OnDestroy {
  @Output() swipe = new EventEmitter<void>();
  @Input() threshold = 80;
  @Input() maxPullDistance = 150;
  @Input() hideDelay = 500;
  @Input() disabled = false;

  isRefreshing = false;
  pullProgress = 0;
  showIndicator = false;
  isResetting = false;
  private _startY = 0;
  private _currentY = 0;
  private _pulling = false;
  private _pullDistance = 0;
  private readonly _isBrowser: boolean;
  private _touchStartHandler: any;
  private _touchMoveHandler: any;
  private _touchEndHandler: any;
  private _touchCancelHandler: any;
  private _listenersAttached = false;
  private _delaySubscription: Subscription;

  constructor(
    public readonly deviceService: DeviceService,
    public readonly windowRefService: WindowRefService,
    private readonly swipeRefreshManager: SwipeRefreshManagerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {
    this._isBrowser = isPlatformBrowser(this.platformId);
  }

  get isTouchDevice(): boolean {
    return this.deviceService.isTouchEnabled();
  }

  get indicatorState(): "idle" | "pulling" | "ready" | "refreshing" {
    if (this.isRefreshing) {
      return "refreshing";
    }
    if (this._pullDistance >= this.threshold) {
      return "ready";
    }
    if (this._pullDistance > 0) {
      return "pulling";
    }
    return "idle";
  }

  ngOnInit(): void {
    if (this._isBrowser && this.isTouchDevice && !this.disabled) {
      this._touchStartHandler = this.onTouchStart.bind(this);
      this._touchMoveHandler = this.onTouchMove.bind(this);
      this._touchEndHandler = this.onTouchEnd.bind(this);
      this._touchCancelHandler = this.onTouchCancel.bind(this);

      this.connect();
      this.swipeRefreshManager.registerComponent(this);

      // Disable browser's pull-to-refresh on supported browsers
      this.disableBrowserPullToRefresh();
    }
  }

  ngOnDestroy(): void {
    if (this._delaySubscription) {
      this._delaySubscription.unsubscribe();
    }
    this.disconnect();
    this.swipeRefreshManager.unregisterComponent(this);
    this.enableBrowserPullToRefresh();
  }

  connect(): void {
    if (this._isBrowser && this.isTouchDevice && !this._listenersAttached) {
      const window = this.windowRefService.nativeWindow;
      // Need passive: false for touchstart to check scroll position
      window.addEventListener("touchstart", this._touchStartHandler, { passive: false });
      // Need passive: false for touchmove to allow preventDefault() for preventing default scroll
      window.addEventListener("touchmove", this._touchMoveHandler, { passive: false });
      // Can use passive: true for touchend as we don't need to prevent default
      window.addEventListener("touchend", this._touchEndHandler, { passive: true });
      // Handle touchcancel for when touch is interrupted
      window.addEventListener("touchcancel", this._touchCancelHandler, { passive: true });
      this._listenersAttached = true;
    }
  }

  disconnect(): void {
    if (this._isBrowser && this._listenersAttached) {
      const window = this.windowRefService.nativeWindow;
      window.removeEventListener("touchstart", this._touchStartHandler);
      window.removeEventListener("touchmove", this._touchMoveHandler);
      window.removeEventListener("touchend", this._touchEndHandler);
      window.removeEventListener("touchcancel", this._touchCancelHandler);
      this._listenersAttached = false;
      // Reset state when disconnecting
      this._pulling = false;
      this.reset();
    }
  }

  private onTouchStart(event: TouchEvent): void {
    if (!this._listenersAttached || !this.isTouchDevice || this.isRefreshing || this.disabled) {
      return;
    }

    // Don't handle touch if image viewer is open
    if (document.body.classList.contains("image-viewer-open")) {
      return;
    }

    // Don't handle touch if user sidebar or main navigation is open
    // Check for active navigation by looking for the active class on the parent containers
    const userNavigationActive = document.querySelector(".user-navigation.active");
    const mainNavigationActive = document.querySelector(".main-navigation.active");

    if (userNavigationActive || mainNavigationActive) {
      return;
    }

    const window = this.windowRefService.nativeWindow;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Only allow pull-to-refresh when at the top of the page
    if (scrollTop > 0) {
      return;
    }

    // If our indicator is still visible (e.g., from a previous pull), prevent browser refresh
    if (this.showIndicator || this.isResetting) {
      event.preventDefault();
    }

    this._startY = event.touches[0].clientY;
    this._pulling = true;
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this._listenersAttached || !this._pulling || !this.isTouchDevice || this.isRefreshing) {
      return;
    }

    // Don't handle touch if image viewer is open
    if (document.body.classList.contains("image-viewer-open")) {
      this._pulling = false;
      return;
    }

    // Don't handle touch if user sidebar or main navigation is open
    // Check for active navigation by looking for the active class on the parent containers
    const userNavigationActive = document.querySelector(".user-navigation.active");
    const mainNavigationActive = document.querySelector(".main-navigation.active");

    if (userNavigationActive || mainNavigationActive) {
      this._pulling = false;
      return;
    }

    this._currentY = event.touches[0].clientY;
    this._pullDistance = Math.max(0, this._currentY - this._startY);

    if (this._pullDistance > 0) {
      // Prevent default immediately to stop browser's native pull-to-refresh
      event.preventDefault();

      this.showIndicator = true;
      const cappedDistance = Math.min(this._pullDistance, this.maxPullDistance);
      this.pullProgress = (cappedDistance / this.threshold) * 100;
      this.changeDetectorRef.markForCheck();
    }
  }

  private onTouchEnd(): void {
    if (!this._listenersAttached || !this._pulling || !this.isTouchDevice) {
      return;
    }

    this._pulling = false;

    if (this._pullDistance >= this.threshold && !this.isRefreshing) {
      this.triggerRefresh();
    } else {
      this.reset();
    }
  }

  private onTouchCancel(): void {
    if (!this._listenersAttached || !this._pulling || !this.isTouchDevice) {
      return;
    }

    // Touch was cancelled (e.g., by browser taking over for native pull-to-refresh)
    this._pulling = false;
    this.reset();
  }

  private triggerRefresh(): void {
    this.isRefreshing = true;
    this.pullProgress = 100;
    this.swipe.emit();

    // Cancel any existing delay
    if (this._delaySubscription) {
      this._delaySubscription.unsubscribe();
    }

    // Reset after animation
    this._delaySubscription = this.utilsService.delay(this.hideDelay).subscribe(() => {
      this.reset();
    });
  }

  private reset(): void {
    // Start the reset animation
    this.isResetting = true;
    this.isRefreshing = false;
    this._pullDistance = 0;
    this.pullProgress = 0;
    this.changeDetectorRef.markForCheck();

    // Then hide the indicator after the animation completes
    this.utilsService.delay(300).subscribe(() => {
      this.showIndicator = false;
      this.isResetting = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  private disableBrowserPullToRefresh(): void {
    if (this._isBrowser) {
      // Add CSS to disable overscroll behavior
      const style = document.createElement("style");
      style.id = "swipe-to-refresh-overscroll";
      style.textContent = `
        html, body {
          overscroll-behavior-y: contain;
          overscroll-behavior-x: contain;
        }
        
        /* Also apply to the main scrollable container if it exists */
        .page, .main-content, [role="main"] {
          overscroll-behavior-y: contain;
        }
      `;
      document.head.appendChild(style);
    }
  }

  private enableBrowserPullToRefresh(): void {
    if (this._isBrowser) {
      // Remove the style element when component is destroyed
      const style = document.getElementById("swipe-to-refresh-overscroll");
      if (style) {
        style.remove();
      }
    }
  }
}
